import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import AddressLabelTools from '@wsb/commerce-address-label-tools';
import ConfigStore from '../stores/Config';
import dataAids from '../constants/dataAids';
import PhoneNumberInput from './PhoneNumberInput';
import MarketingConsent from './MarketingConsent';
import { trackMetric } from '../../common/utils/trackMetric';
import {
  BOOKING_FORM_LOGIN_CTA_CLICK,
  BOOKING_FORM_CUSTOMER_LOCATION_FORM_IMPRESSION
} from '../constants/metrics';

const formCellStyles = {
  maxWidth: '100%',
  flexBasis: '100%',
  flexShrink: '1',
  flexGrow: '1',
  paddingHorizontal: 'xsmall'
};

const styles = {
  form: {
    marginBottom: 'large'
  },
  formLoginText: {
    paddingTop: 'xxsmall'
  },
  formCellSm50: {
    ...formCellStyles,
    '@sm': {
      maxWidth: '50%',
      flexBasis: '50%',
      paddingHorizontal: 'small'
    }
  },
  formCellSm100: {
    ...formCellStyles,
    '@sm': {
      maxWidth: '100%',
      flexBasis: '100%',
      paddingHorizontal: 'small'
    }
  },
  inputBlock: {
    marginBottom: 'medium'
  },
  inputBlockMobile: {
    '@xs-only': {
      marginBottom: 'medium'
    }
  },
  inputError: {
    paddingTop: 'xsmall'
  },
  formGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: '-xsmall',
    marginRight: '-xsmall',
    '@sm': {
      marginLeft: '-small',
      marginRight: '-small'
    }
  }
};

export default class ClientForm extends Component {
  componentDidMount() {
    const { location, countryStates, businessCountry, onUpdateLocation } = this.props;

    if (location.state) {
      onUpdateLocation('state', { id: location.state.id });
    } else if (businessCountry.states_required) {
      setTimeout(() => {
        onUpdateLocation('state', countryStates.length ? { id: countryStates[0].id } : null);
      }, 0);
    }

    if (!location.country) {
      setTimeout(() => {
        onUpdateLocation('country', { id: businessCountry.id });
      }, 0);
    }

    if (this.isCustomerLocationFormShown) {
      trackMetric(BOOKING_FORM_CUSTOMER_LOCATION_FORM_IMPRESSION);
    }
  }

  getAuthUrl({ logout = false } = {}) {
    const { loginRedirectUrl } = this.props;
    const encodedUrl = encodeURIComponent(loginRedirectUrl);

    if (logout) {
      return `/m/logout?r=${encodedUrl}`;
    }

    return `/m/login?r=${encodedUrl}`;
  }

  districtLabel() {
    const {
      staticContent,
      businessCountry: { cities_required: citiesRequired }
    } = this.props;

    return citiesRequired ? staticContent.district : staticContent.districtOptional;
  }

  cityLabel() {
    const {
      staticContent,
      businessCountry: { cities_required: citiesRequired }
    } = this.props;

    return citiesRequired ? staticContent.city : staticContent.cityOptional;
  }

  get isGopayCartOn() {
    return ConfigStore.isGopayCartOn();
  }

  get formTitle() {
    const { staticContent } = this.props;

    if (this.isGopayCartOn) {
      return staticContent.serviceLocation;
    }

    return staticContent.yourInformation;
  }

  get isCustomerLocationFormShown() {
    const { service } = this.props;

    return service.location_type === 'customer';
  }

  /* eslint-disable complexity */
  render() {
    const {
      titleStyles,
      error,
      client,
      location,
      staticContent,
      onUpdateClient,
      onUpdateLocation,
      businessCountry,
      countryStates,
      isC2PhoneRequired,
      isC2LoggedIn,
      isMembershipOn,
      membershipAccountsOn,
      marketing
    } = this.props;

    const requiresConsent = this.props.requiresConsent && !this.isGopayCartOn;

    const {
      Element: {
        Block,
        Heading,
        Details,
        Text,
        Input,
        Input: { Select },
        Link,
        Error
      },
      Group: { Form }
    } = UX2;

    const { first_name: firstName, last_name: lastName, phone_number: phoneNumber, email } = client;

    const { address_1: address1, address_2: address2, city, postal_code: postalCode } = location;

    const isAuthEnabled = membershipAccountsOn && isMembershipOn && !this.isGopayCartOn;
    const isLogInLinkShown = Boolean(isAuthEnabled && !isC2LoggedIn);
    const isLogOutLinkShown = Boolean(isAuthEnabled && isC2LoggedIn);

    return (
      <Block style={ styles.form }>
        <Block style={ titleStyles }>
          <Heading.Minor>{ this.formTitle }</Heading.Minor>
          { isLogOutLinkShown && (
            <Details style={ styles.formLoginText }>
              { email }&nbsp;
              <Link
                data-aid={ dataAids.BOOKING_FORM_LOGOUT_LINK }
                href={ this.getAuthUrl({ logout: true }) }
              >
                { staticContent.signOut }
              </Link>
            </Details>
          ) }
          { isLogInLinkShown && (
            <Details style={ styles.formLoginText }>
              { staticContent.haveAccount }&nbsp;
              <Link
                data-aid={ dataAids.BOOKING_FORM_LOGIN_LINK }
                onClick={ () => trackMetric(BOOKING_FORM_LOGIN_CTA_CLICK) }
                href={ this.getAuthUrl() }
              >
                { staticContent.signIn }
              </Link>
            </Details>
          ) }
        </Block>
        <Form>
          { !this.isGopayCartOn && (
            <Fragment>
              <Block style={ styles.inputBlock }>
                <Input
                  data-aid={ dataAids.BOOKING_FORM_FIRST_NAME_INPUT }
                  placeholder={ staticContent.firstName }
                  value={ firstName }
                  onChange={ e => onUpdateClient('first_name', e.target.value) }
                  maxLength='255'
                />
                { error && error.type === 'blank_first_name' && (
                  <Error style={ styles.inputError }>{ staticContent.firstNameValidationError }</Error>
                ) }
              </Block>
              <Block style={ styles.inputBlock }>
                <Input
                  data-aid={ dataAids.BOOKING_FORM_LAST_NAME_INPUT }
                  placeholder={ staticContent.lastName }
                  value={ lastName }
                  onChange={ e => onUpdateClient('last_name', e.target.value) }
                  maxLength='255'
                />
                { error && error.type === 'blank_last_name' && (
                  <Error style={ styles.inputError }>{ staticContent.lastNameValidationError }</Error>
                ) }
              </Block>
              <Block
                style={{
                  ...styles.inputBlock,
                  marginBottom: requiresConsent ? 'xsmall' : 'medium'
                }}
              >
                <Input
                  data-aid={ dataAids.BOOKING_FORM_EMAIL_INPUT }
                  type='email'
                  placeholder={ staticContent.emailAddress }
                  value={ email }
                  onChange={ e => onUpdateClient('email', e.target.value) }
                  maxLength='255'
                  disabled={ isC2LoggedIn }
                />
                { error && error.type === 'invalid_email' && (
                  <Error data-aid={ dataAids.BOOKING_FORM_INPUT_ERROR } style={ styles.inputError }>
                    { staticContent.emailValidationError }
                  </Error>
                ) }
              </Block>
              { requiresConsent && (
                <MarketingConsent marketing={ marketing } staticContent={ staticContent } />
              ) }
              <Block style={ styles.inputBlock }>
                <PhoneNumberInput
                  data-aid={ dataAids.BOOKING_FORM_PHONE_FIELD_RENDERED }
                  placeholder={
                    isC2PhoneRequired
                      ? staticContent.phoneNumber
                      : staticContent.phoneNumberOptional
                  }
                  value={ phoneNumber }
                  countryISO3={ businessCountry.iso3 }
                  onChange={ newPhoneNumber => onUpdateClient('phone_number', newPhoneNumber) }
                  maxLength='255'
                />
                { error && error.type === 'blank_phone' && (
                  <Error style={ styles.inputError } data-aid={ dataAids.BOOKING_FORM_INPUT_ERROR }>
                    { staticContent.phoneNumberValidationError }
                  </Error>
                ) }
              </Block>
            </Fragment>
          ) }
          { this.isCustomerLocationFormShown && (
            <Block data-aid={ dataAids.CUSTOMER_LOCATION_FIELDS_RENDERED }>
              <Block style={ styles.inputBlock }>
                { !this.isGopayCartOn && (
                  <Text style={{ paddingBottom: 'small' }}>{ staticContent.serviceLocation }</Text>
                ) }
                <Input
                  data-aid={ dataAids.BOOKING_FORM_ADDRESS1_INPUT }
                  placeholder={ staticContent.address1 }
                  value={ address1 }
                  onChange={ e => onUpdateLocation('address_1', e.target.value) }
                  maxLength='255'
                />
                { error && error.type === 'blank_address_1' && (
                  <Error data-aid={ dataAids.BOOKING_FORM_INPUT_ERROR } style={ styles.inputError }>
                    { staticContent.addressValidationError }
                  </Error>
                ) }
              </Block>
              <Block style={ styles.inputBlock }>
                <Input
                  data-aid={ dataAids.BOOKING_FORM_ADDRESS2_INPUT }
                  placeholder={ staticContent.address2 }
                  value={ address2 }
                  onChange={ e => onUpdateLocation('address_2', e.target.value) }
                  maxLength='255'
                />
              </Block>
              <Block style={ styles.inputBlock }>
                <Input
                  data-aid={ dataAids.BOOKING_FORM_CITY_INPUT }
                  placeholder={ AddressLabelTools.getCityLabel({
                    countryIso: businessCountry.iso,
                    i18n: {
                      city: this.cityLabel(),
                      district: this.districtLabel()
                    }
                  }) }
                  value={ city }
                  onChange={ e => onUpdateLocation('city', e.target.value) }
                  maxLength='255'
                />
                { error && error.type === 'blank_city' && (
                  <Error data-aid={ dataAids.BOOKING_FORM_INPUT_ERROR } style={ styles.inputError }>
                    { staticContent.cityValidationError }
                  </Error>
                ) }
              </Block>
              <Block style={ styles.formGrid }>
                { countryStates.length > 0 && (
                  <Block
                    style={
                      businessCountry.postal_codes_required
                        ? styles.formCellSm50
                        : styles.formCellSm100
                    }
                  >
                    <Block style={ styles.inputBlockMobile }>
                      <Select
                        value={ location.state ? location.state.id : null }
                        onChange={ e => onUpdateLocation('state', { id: Number(e.target.value) }) }
                      >
                        { countryStates.map(state => {
                          return (
                            <option value={ state.id } key={ state.id }>
                              { staticContent.states[`${businessCountry.iso}-${state.abbr}`] }
                            </option>
                          );
                        }) }
                      </Select>
                    </Block>
                  </Block>
                ) }
                { businessCountry.postal_codes_required && (
                  <Block style={ countryStates.length ? styles.formCellSm50 : styles.formCellSm100 }>
                    <Input
                      data-aid={ dataAids.BOOKING_FORM_POSTAL_CODE_INPUT }
                      placeholder={ staticContent.postal }
                      value={ postalCode }
                      onChange={ e => onUpdateLocation('postal_code', e.target.value) }
                      maxLength='255'
                    />
                    { error && error.type === 'blank_postal_code' && (
                      <Error data-aid={ dataAids.BOOKING_FORM_INPUT_ERROR } style={ styles.inputError }>
                        { staticContent.postalCodeValidationError }
                      </Error>
                    ) }
                  </Block>
                ) }
              </Block>
            </Block>
          ) }
        </Form>
      </Block>
    );
  }
  /* eslint-enable complexity */
}

ClientForm.propTypes = {
  titleStyles: PropTypes.object,
  error: PropTypes.object,
  client: PropTypes.object,
  location: PropTypes.object,
  service: PropTypes.object,
  staticContent: PropTypes.object.isRequired,
  onUpdateClient: PropTypes.func,
  onUpdateLocation: PropTypes.func,
  businessCountry: PropTypes.object,
  countryStates: PropTypes.array,
  isC2PhoneRequired: PropTypes.bool,
  isC2LoggedIn: PropTypes.bool,
  isMembershipOn: PropTypes.bool,
  loginRedirectUrl: PropTypes.string.isRequired,
  membershipAccountsOn: PropTypes.bool,
  marketing: PropTypes.bool.isRequired,
  requiresConsent: PropTypes.bool.isRequired
};
